import type { BlockQuestion, ContactBlockConfig } from '../types/templateFormFields';
import type { HiddenContactFields } from '../types/templateForms';

/**
 * Ready-made contact blocks. The Director, Officer and Shareholder presets replace the v1
 * incorporation "wrapping" custom fields (numberOfShareholders + firstNameShareholder, ...,
 * numberOfDirectors + ..., etc.).
 *
 * Contact fields and nested questions mirror the global ForProfitIncorporation defaults
 * (docs/default-incorporation-questions.json). Fax is dropped since contacts have no fax field.
 *
 * The Blank preset has no contact fields and no questions: a repeatable set of custom fields the
 * builder adds, shown as stacked entries instead of table columns.
 */
export const CONTACT_BLOCK_PRESET_KINDS = ['Director', 'Officer', 'Shareholder', 'Blank'] as const;
export type ContactBlockPresetKind = (typeof CONTACT_BLOCK_PRESET_KINDS)[number];

interface ContactBlockPreset {
    /** Default question title, used when the builder hasn't entered one. Empty leaves it to them. */
    title: string;
    /** Whether the block needs at least one entry. A corporation may have no officers. */
    isRequired: boolean;
    config: ContactBlockConfig;
}

/** v1 asked each person for name, address, unit, postal code, cell, fax and email only. */
const HIDDEN_CONTACT_FIELDS: HiddenContactFields = {
    firstId: true,
    secondId: true,
    homePhone: true,
    dateOfBirth: true,
    maritalStatus: true,
    citizenshipStatus: true,
    gender: true,
    preferredPronouns: true,
    occupation: true,
    employer: true,
    employerAddress: true,
    employerUnit: true,
    employerPhone: true,
};

/** Directors and officers were also asked for their occupation. */
const HIDDEN_CONTACT_FIELDS_WITH_OCCUPATION: HiddenContactFields = {
    ...HIDDEN_CONTACT_FIELDS,
    occupation: false,
};

const SHAREHOLDER_QUESTIONS: BlockQuestion[] = [
    {
        name: 'percentageOfShares',
        title: 'Percentage of Shares to own',
        questionSchema: {
            name: 'percentageOfShares',
            type: 'percentage',
            label: 'Percentage of Shares to own',
            isCustomQuestion: true,
        },
    },
    {
        name: 'numberOfShares',
        title: 'Number of shares',
        questionSchema: {
            name: 'numberOfShares',
            type: 'text',
            label: 'Number of shares',
            isCustomQuestion: true,
        },
    },
    {
        name: 'classOfShares',
        title: 'Class of shares',
        questionSchema: {
            name: 'classOfShares',
            type: 'text',
            label: 'Class of shares',
            isCustomQuestion: true,
        },
    },
    {
        name: 'subscriptionPricePerShare',
        title: 'Subscription price per share',
        questionSchema: {
            name: 'subscriptionPricePerShare',
            type: 'currency',
            label: 'Subscription price per share',
            isCustomQuestion: true,
        },
    },
];

const DIRECTOR_OFFICE_DESCRIPTION =
    'Office to be held by each Director who will be an Officer (e.g., President, Secretary, Treasurer, VP, etc). Typically a corporation has at least a President and Secretary. The inclusion of other offices is optional. One individual may hold more than one office';

const DIRECTOR_QUESTIONS: BlockQuestion[] = [
    {
        name: 'officeToBeHeldDirector',
        title: 'Office to Be Held',
        subTitle: DIRECTOR_OFFICE_DESCRIPTION,
        questionSchema: {
            name: 'officeToBeHeldDirector',
            type: 'text',
            label: 'Office to Be Held',
            description: DIRECTOR_OFFICE_DESCRIPTION,
            isCustomQuestion: true,
        },
    },
    {
        name: 'residentOfCanadaDirector',
        title: 'Resident of Canada',
        questionSchema: {
            name: 'residentOfCanadaDirector',
            type: 'radio',
            label: 'Resident of Canada',
            options: [
                { optionLabel: 'Yes', optionValue: 'Yes' },
                { optionLabel: 'No', optionValue: 'No' },
                { optionLabel: 'Unsure', optionValue: 'Unsure' },
            ],
            radioOrientation: 'horizontal',
            isCustomQuestion: true,
        },
    },
];

const OFFICES = [
    'Vice-President',
    'Treasurer',
    'Secretary/Treasurer',
    'Secretary',
    'President/Secretary/Treasurer',
    'President',
    'Chief Operations Officer',
    'Chief Financial Officer',
    'Chief Executive Officer',
    'President/Secretary',
    'President/Treasurer',
    'Chairman',
    'Chief Technology Officer',
    'Vice-President/Secretary',
    'Other',
];

const OFFICER_OFFICE_DESCRIPTION =
    'Typically a corporation has at least a President and Secretary. The inclusion of other offices is optional. One individual may hold more than one office';

const OFFICER_QUESTIONS: BlockQuestion[] = [
    {
        name: 'officeToBeHeld',
        title: 'Office to Be Held',
        subTitle: OFFICER_OFFICE_DESCRIPTION,
        questionSchema: {
            name: 'officeToBeHeld',
            type: 'select',
            label: 'Office to Be Held',
            description: OFFICER_OFFICE_DESCRIPTION,
            selectOptions: OFFICES.map((office) => ({
                selectItemKey: office,
                selectItemLabel: office,
                selectItemValue: office,
            })),
            conditions: [{ value: 'Other', nextQuestion: { name: 'otherOfficeToBeHeld' } }],
            isCustomQuestion: true,
        },
    },
    {
        name: 'otherOfficeToBeHeld',
        title: 'Other',
        questionSchema: {
            name: 'otherOfficeToBeHeld',
            type: 'text',
            label: 'Other',
            isConditionalQuestion: true,
            conditionedBy: 'officeToBeHeld',
            conditionValue: 'Other',
        },
    },
];

export const CONTACT_BLOCK_PRESETS: Record<ContactBlockPresetKind, ContactBlockPreset> = {
    Director: {
        title: 'Director details',
        isRequired: true,
        config: {
            entryLabel: 'Director',
            hiddenFields: HIDDEN_CONTACT_FIELDS_WITH_OCCUPATION,
            questions: DIRECTOR_QUESTIONS,
        },
    },
    Officer: {
        title: 'Officer details',
        isRequired: false,
        config: {
            entryLabel: 'Officer',
            hiddenFields: HIDDEN_CONTACT_FIELDS_WITH_OCCUPATION,
            questions: OFFICER_QUESTIONS,
        },
    },
    Shareholder: {
        title: 'Shareholder details',
        isRequired: true,
        config: {
            entryLabel: 'Shareholder',
            hiddenFields: HIDDEN_CONTACT_FIELDS,
            questions: SHAREHOLDER_QUESTIONS,
        },
    },
    Blank: {
        title: '',
        isRequired: false,
        config: {
            entryLabel: 'Entry',
            hideContactFields: true,
            questions: [],
        },
    },
};

/** A fresh copy of a preset's block config, safe to edit. */
export function createContactBlockPresetConfig(kind: ContactBlockPresetKind): ContactBlockConfig {
    return structuredClone(CONTACT_BLOCK_PRESETS[kind].config);
}
